import { ExcelExport, Finding, FindingComment, FindingCommentType } from '@consigli/types';
import { format } from 'date-fns';
import { TFunction } from 'i18next';

import { getFindingMessage } from './finding-message';

export interface MilestoneExportRow {
  id: number;
  milestone: string;
  date: Date;
  status: string;
}

export interface DeliverableExportItem {
  category: string;
  name: string;
  description: string;
  source: string | null;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const day = date.getDate();
  // getMonth is 0-indexed, so have to + 1
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).substring(2);

  return `${hours}:${minutes}, ${day}/${month}/${year}`;
};

const formatComments = (comments: FindingComment[], t: TFunction) => {
  return comments
    .map((comment) => {
      const authorName =
        comment.type === FindingCommentType.SYSTEM_COMMENT_FORWARDED
          ? 'Consigli'
          : comment.authorName;

      const commentText =
        comment.type !== FindingCommentType.USER_COMMENT
          ? t(`findings.comment-codes.${comment.type.toLowerCase()}`, {
              commentText: comment.text,
            })
          : comment.text;

      const createdAt = comment.createdAt ? formatDate(comment.createdAt) : '';

      return `- ${authorName}: ${commentText} - ${createdAt}`;
    })
    .join('\r\n');
};

export const createFindingsExcelExport = (
  findings: Finding[],
  t: TFunction,
  projectName: string,
): ExcelExport => {
  const title = projectName;
  const headers = [
    'ID',
    t('findings.export.status'),
    t('findings.export.conflict-type'),
    t('findings.export.document'),
    t('findings.export.comment'),
    t('findings.export.page-number'),
    t('findings.export.content'),
    t('findings.export.ref-content'),
    t('findings.export.ref-document'),
    t('findings.export.comments'),
  ];
  const sortedFindings = [...findings].sort((a, b) => a.id - b.id);
  const data = sortedFindings.map((finding) => {
    return [
      finding.id,
      t(`findings.status.${finding.status}`),
      t(`dashboard.conflict-title.${finding.conflictType}`),
      finding.fileName ? finding.fileName : t('findings.project-related-finding'),
      getFindingMessage(finding, finding.messageContext, t),
      finding.pageNumber !== null && finding.pageNumber !== undefined ? finding.pageNumber + 1 : '',
      finding.content ? finding.content : '',
      finding.refContent ? finding.refContent : '',
      finding.refFileName ? finding.refFileName : '',
      finding.comments ? formatComments(finding.comments, t) : '',
    ];
  });
  return { title, headers, data };
};

export const createRfpMilestonesExport = (
  milestones: MilestoneExportRow[],
  t: TFunction,
  projectName: string,
): ExcelExport => {
  const title = `${projectName} - ${t('dashboard.rfp-timeline.heading')}`;
  const headers = [
    'ID',
    t('dashboard.rfp-export.milestone'),
    t('dashboard.rfp-export.date'),
    t('dashboard.rfp-export.status'),
  ];
  const sortedMilestones = [...milestones].sort((a, b) => a.date.getTime() - b.date.getTime());
  const data = sortedMilestones.map((row) => [
    row.id,
    row.milestone,
    format(row.date, 'dd/MM/yyyy'),
    t(`dashboard.rfp-export.status-${row.status}`),
  ]);
  return { title, headers, data };
};

export const createRfpDeliverablesExport = (
  deliverables: DeliverableExportItem[],
  t: TFunction,
  projectName: string,
): ExcelExport => {
  const title = `${projectName} - ${t('dashboard.rfp-requirements.heading')}`;
  const headers = [
    t('dashboard.rfp-export.category'),
    t('dashboard.rfp-export.deliverable-name'),
    t('dashboard.rfp-export.description'),
    t('dashboard.rfp-export.source'),
  ];
  const data = deliverables.map((item) => [
    item.category,
    item.name,
    item.description,
    item.source ?? '',
  ]);
  return { title, headers, data };
};
